import Cookies from 'cookie-universal';
import LocalStorage from '@/components/commons/LocalStorage';
import CountryUtils from '@/components/commons/CountryUtils';

const KEY_ARTICLE_GEO_LOCATION = 'article-geo-location';
const KEY_ARTICLE_ZIP_LOCATION = 'article-zip-location';
const KEY_ARTICLE_LOCATION_MANUAL = 'article-location-manual';

const ARTICLE_CURRENCY = 'currency';

const DEFAULT_LOCATION_US = {
  accuracyRadius: 100,
  countryId: 2,
  countryCode: 'US',
  regionId: 5,
  regionCode: 'CA',
  city: 'Los Angeles',
  currencyId: 1,
  zipcode: '90014',
};

const DEFAULT_LOCATION_CA = {
  accuracyRadius: 100,
  countryId: 1,
  countryCode: 'CA',
  regionId: 52,
  regionCode: 'BC',
  city: 'Vancouver',
  currencyId: 2,
  zipcode: 'V6Z 2H7',
};

export default class LocationService {
  static #geoLocation = null;

  static #isOffice = false;

  static #zipLocation = null;

  static #isLocationManual = false;

  static #serverCurrencyId = null;

  static setLocation(locationData, shouldReload = true) {
    if (locationData) {
      const { geoLocation, zipLocation } = locationData;

      LocationService.setGeoLocation(geoLocation);
      LocationService.setZipLocation(zipLocation);

      LocationService.setCookieCurrencyId((zipLocation || geoLocation)?.currencyId, {
        forceReplace: false,
        shouldReload,
      });
    }
  }

  static setGeoLocation(geoLocation) {
    if (geoLocation) {
      try {
        LocalStorage.set(KEY_ARTICLE_GEO_LOCATION, JSON.stringify(geoLocation));
      } catch (error) {
        console.log(error);
      }
    } else {
      LocalStorage.remove(KEY_ARTICLE_GEO_LOCATION);
    }

    LocationService.#geoLocation = geoLocation;
  }

  static getGeoLocation() {
    if (LocationService.#geoLocation) {
      return LocationService.#geoLocation;
    }

    try {
      LocationService.#geoLocation = JSON.parse(LocalStorage.get(KEY_ARTICLE_GEO_LOCATION));
      return LocationService.#geoLocation;
    } catch (error) {
      return null;
    }
  }

  static setZipLocation(zipLocation) {
    if (zipLocation) {
      try {
        LocalStorage.set(KEY_ARTICLE_ZIP_LOCATION, JSON.stringify(zipLocation));
      } catch (error) {}
    } else {
      LocalStorage.remove(KEY_ARTICLE_ZIP_LOCATION);
    }

    LocationService.#zipLocation = zipLocation;
  }

  static getZipLocation() {
    if (LocationService.#zipLocation) {
      return LocationService.#zipLocation;
    }

    try {
      LocationService.#zipLocation = JSON.parse(LocalStorage.get(KEY_ARTICLE_ZIP_LOCATION));
      return LocationService.#zipLocation;
    } catch (error) {
      return null;
    }
  }

  static setIsLocationManual(isLocationManual) {
    if (typeof isLocationManual !== 'boolean') {
      return;
    }

    if (isLocationManual) {
      LocalStorage.set(KEY_ARTICLE_LOCATION_MANUAL, isLocationManual);
    } else {
      LocalStorage.remove(KEY_ARTICLE_LOCATION_MANUAL);
    }

    LocationService.#isLocationManual = isLocationManual;
  }

  static getIsLocationManual() {
    if (LocationService.#isLocationManual) {
      return LocationService.#isLocationManual;
    }

    try {
      LocationService.#isLocationManual = !!JSON.parse(LocalStorage.get(KEY_ARTICLE_LOCATION_MANUAL));
      return LocationService.#isLocationManual;
    } catch (error) {
      return false;
    }
  }

  static getPreferredLocation() {
    if (LocationService.getZipLocation() && LocationService.getIsLocationManual()) {
      return LocationService.getZipLocation();
    }

    if (
      (LocationService.getGeoLocation()?.accuracyRadius ?? 10000) <= 1000 &&
      LocationService.getGeoLocation()?.currencyId == LocationService.getCurrencyId()
    ) {
      return LocationService.getGeoLocation();
    }

    if (LocationService.isUS()) {
      return DEFAULT_LOCATION_US;
    }

    return DEFAULT_LOCATION_CA;
  }

  static setOffice(isOffice) {
    LocationService.#isOffice = isOffice;
  }

  static isOffice() {
    return LocationService.#isOffice;
  }

  static setCountry(country, { shouldReload = true }) {
    const currentCurrencyId = LocationService.getCurrencyId();
    if (country && country.currencyId != currentCurrencyId) {
      LocationService.setIsLocationManual(false);
      LocationService.setZipLocation(null);
      LocationService.setCookieCurrencyId(country.currencyId, {
        shouldReload,
      });
    }
  }

  static getCurrencyId() {
    const currencyId = LocationService.#serverCurrencyId || Cookies().get(ARTICLE_CURRENCY);
    if (currencyId != null) {
      return parseInt(currencyId, 10);
    }

    return DEFAULT_LOCATION_US.currencyId;
  }

  static getCountryId() {
    const currencyId = LocationService.#serverCurrencyId || Cookies().get(ARTICLE_CURRENCY);
    if (currencyId != null) {
      return CountryUtils.getCountryByCurrencyId(currencyId).id;
    }

    return DEFAULT_LOCATION_US.countryId;
  }

  static setCookieCurrencyId(id, { forceReplace = true, shouldReload = true }) {
    const currencyId = LocationService.#serverCurrencyId || Cookies().get(ARTICLE_CURRENCY);

    try {
      if (forceReplace || !currencyId || id != currencyId) {
        Cookies().set(ARTICLE_CURRENCY, id, {
          path: '/',
          maxAge: 3.154e7,
        });
      }
    } catch (error) {}

    if (shouldReload && id != currencyId) {
      window.location.reload();
    }
  }

  static setServerCurrencyId(id) {
    LocationService.#serverCurrencyId = id;
  }

  static isUS() {
    return LocationService.getCountryId() === DEFAULT_LOCATION_US.countryId;
  }

  static isCanada() {
    return LocationService.getCountryId() === DEFAULT_LOCATION_CA.countryId;
  }
}
